import React, { useContext, useState, useEffect } from 'react';
import styled, { useTheme, css } from 'styled-components';
import { CopyFineprintSkin } from '../../atoms/Copytext/CopytextSkins';
import UrlContext from '~/helpers/UrlContext';
import { gridGutter, mq } from '~/styles/grid';
import PageTypeContext from '~/helpers/PageTypeContext';
import { NavLeftDistance } from '~/components/molecules/Navigation/Desktop/MetaNavigation/MetaNavigationDesktop/MetaNavigationDesktop.styles';
import { unicodes } from '~/components/organisms/Quote/Quote.helpers';
import { useIntl } from 'react-intl';
import useIsMobile from '~/hooks/useIsMobile';

const BreadcrumbStyled = styled.div`
  position: absolute;
  z-index: 11;
  width: 100%;
  height: 37px;
  background: ${(/** @type {{theme: Theme}} */ props) =>
    props.theme.breadcrumb.bgColor};
`;

const BreadcrumbContainer = styled.div`
  padding: 5px 15px 5px ${NavLeftDistance}px;

  @media (${mq.lgDown}) {
    padding: 5px ${gridGutter}px;

    ${(props) =>
      !props.mounted &&
      css`
        visibility: hidden;
        overflow: hidden;
        white-space: nowrap;
      `}
  }
`;

const Segment = styled.a`
  ${CopyFineprintSkin};
  color: ${(props) => props.theme.color.white500};
  text-decoration: none;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
  &::after {
    content: '>';
    padding: 0 10px;
  }
  &:last-child::after {
    content: '';
  }
`;

const LastSegment = styled.span`
  ${CopyFineprintSkin};
  color: ${(props) => props.theme.color.white500};
`;

/**
 * @param {Object} props
 * @param {NavLinkProps[]} props.path
 * @returns {React.ReactElement}
 */
const RenderPath = ({ path }) => {
  const theme = useTheme();
  const createUrl = useContext(UrlContext);
  const [fullText, setFullText] = useState(false);
  const isMobile = useIsMobile({ contextKey: 'breadcrumb' });

  const shortenText = (str, index) => {
    const maxLength = theme.breadcrumb.maxSegmentLength;
    const drawSegments =
      path.length > 2 ? [0, path.length - 2, path.length - 1] : [0, 1];

    if (drawSegments.includes(index)) {
      if (str.length > maxLength) {
        const lastSpaceIndex = str.substring(0, maxLength).lastIndexOf(' ');
        return `${str.substring(0, lastSpaceIndex)} ${
          unicodes.HorizontalEllipsis
        }`;
      }
      return str;
    }
    const txt = fullText ? str : unicodes.HorizontalEllipsis;
    return txt;
  };

  const fullPathHandler = (e) => {
    e.preventDefault();
    setFullText(true);
  };

  if (isMobile && path.length > 1) {
    const parent = path[path.length - 2];
    return <Segment href={createUrl(parent.href)}>&lt; {parent.text}</Segment>;
  }

  return (
    <>
      {path.map((segment, index) => {
        if (index === path.length - 1) {
          return (
            <LastSegment key={segment.text}>
              {shortenText(segment.text, index)}
            </LastSegment>
          );
        }
        return (
          <Segment
            key={segment.text}
            href={createUrl(segment.href)}
            onClick={(e) => {
              if (
                shortenText(segment.text, index) ===
                  unicodes.HorizontalEllipsis &&
                !fullText
              ) {
                fullPathHandler(e);
              }
            }}
          >
            {shortenText(segment.text, index)}
          </Segment>
        );
      })}
    </>
  );
};

/**
 * @param {BreadcrumbProps} props
 * @returns {React.ReactElement} The rendered component
 */
const Breadcrumb = ({ path, className }) => {
  const intl = useIntl();
  const homePageText = intl.formatMessage({
    id: 'main.homePageText',
  });
  const pageType = useContext(PageTypeContext);
  const createUrl = React.useContext(UrlContext);
  const [mounted, mount] = useState(false);
  const normalizedPath = path.map((segment) => ({
    ...segment,
    href: segment.href ?? segment.url,
  }));
  const pathWithHomeLink = [
    {
      href: createUrl('/'),
      text: homePageText,
      target: '_self',
      title: homePageText,
    },
    ...normalizedPath,
  ];

  useEffect(() => {
    setTimeout(() => {
      mount(true);
    }, 500);
  }, []);
  return (
    <BreadcrumbStyled
      data-id="breadcrumb"
      className={className}
      pageType={pageType}
    >
      <BreadcrumbContainer mounted={mounted}>
        <RenderPath path={pathWithHomeLink} />
      </BreadcrumbContainer>
    </BreadcrumbStyled>
  );
};

export default Breadcrumb;
